import {
    Alert,
    Box,
    Button,
    Center,
    Checkbox,
    Divider,
    Group,
    Image,
    List,
    Loader,
    Modal,
    Paper,
    Table,
    Text,
    TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCheck, IconPhoto, IconPlayerPlay } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import type { FileValidationResult } from '../../data/media';
import { adjustToMonday } from '../../lib/date-utils';
import { plural } from '../../lib/utils';

export interface PendingCreative {
    file: File;
    id: string;
    marketingFields: {
        week: string;
        hasTextOverlay: boolean;
        hasCallToAction: boolean;
        additionalText?: string;
    };
    validation?: FileValidationResult;
}

interface MarketingFieldsPopupProps {
    creatives: PendingCreative[];
    onSubmit: (creatives: PendingCreative[]) => Promise<void>;
    onCancel: () => void;
    isUploading: boolean;
    opened: boolean;
    onClose: () => void;
}

export const MarketingFieldsPopup = ({
    creatives,
    onSubmit,
    onCancel,
    isUploading,
    opened,
    onClose,
}: MarketingFieldsPopupProps) => {
    const [pendingCreatives, setPendingCreatives] = useState<PendingCreative[]>(creatives);

    useEffect(() => {
        setPendingCreatives(creatives);
    }, [creatives]);

    const updateCreativeField = (
        id: string,
        field: keyof PendingCreative['marketingFields'],
        value: string | boolean,
    ) => {
        let finalValue = value;

        // If updating the week field, ensure it's a Monday
        if (field === 'week' && typeof value === 'string' && value) {
            finalValue = adjustToMonday(value).format('YYYY-MM-DD');
        }

        setPendingCreatives((prev) =>
            prev.map((creative) =>
                creative.id === id
                    ? {
                          ...creative,
                          marketingFields: {
                              ...creative.marketingFields,
                              [field]: finalValue,
                          },
                      }
                    : creative,
            ),
        );
    };

    const requiredFields = ['week'] as const;

    const hasRequiredFields = (creative: PendingCreative) => {
        return requiredFields.every((field) => creative.marketingFields[field].trim() !== '');
    };

    const allCreativesValid = pendingCreatives.every(hasRequiredFields);

    const hasFieldError = (creative: PendingCreative, field: keyof PendingCreative['marketingFields']) => {
        const isRequired = requiredFields.includes(field as any);
        const isEmpty =
            typeof creative.marketingFields[field] === 'string' ? creative.marketingFields[field].trim() === '' : false;
        return isRequired && isEmpty;
    };

    const handleSubmit = async () => {
        if (!allCreativesValid) {
            return;
        }
        await onSubmit(pendingCreatives);
    };

    const creativesWithWarnings = pendingCreatives.filter(
        (creative) => creative.validation && creative.validation.warnings.length > 0,
    );

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size={1400}
            yOffset={32}
            closeOnClickOutside={false}
            closeOnEscape={!isUploading}
            withCloseButton={!isUploading}
            title={`Configure Marketing Fields for ${creatives.length} ${plural(creatives.length, 'Creative', 'Creatives')}`}
        >
            {/* Show warnings if any */}
            {creativesWithWarnings.length > 0 && (
                <Alert color="yellow" title="Recommendations" mb="md">
                    <List spacing={4}>
                        {creativesWithWarnings.map((creative) => (
                            <List.Item key={creative.id}>
                                <strong>{creative.file.name}:</strong> {creative.validation!.warnings.join(', ')}
                            </List.Item>
                        ))}
                    </List>
                </Alert>
            )}

            <Paper withBorder radius="sm" style={{ overflow: 'hidden' }}>
                <Table.ScrollContainer minWidth={800} mah={384}>
                    <Table stickyHeader verticalSpacing="xs" withColumnBorders>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{ fontWeight: 500 }}>File</Table.Th>
                                <Table.Th style={{ fontWeight: 500 }}>Week</Table.Th>
                                <Table.Th style={{ textAlign: 'center', fontWeight: 500 }}>Text</Table.Th>
                                <Table.Th style={{ textAlign: 'center', fontWeight: 500 }}>CTA</Table.Th>
                                <Table.Th style={{ fontWeight: 500 }}>Freeholder</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {pendingCreatives.map((creative) => (
                                <Table.Tr key={creative.id}>
                                    {/* File Preview and Info */}
                                    <Table.Td>
                                        <Group gap="sm">
                                            <Box style={{ flexShrink: 0 }}>
                                                <Paper
                                                    withBorder
                                                    radius="sm"
                                                    bg="light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))"
                                                    c="dimmed"
                                                    w={48}
                                                    h={48}
                                                    style={{
                                                        overflow: 'hidden',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    {creative.file.type.startsWith('image/') ? (
                                                        <Image
                                                            src={URL.createObjectURL(creative.file)}
                                                            alt={creative.file.name}
                                                            w="100%"
                                                            h="100%"
                                                            fit="cover"
                                                        />
                                                    ) : creative.file.type.startsWith('video/') ? (
                                                        <Box pos="relative" w="100%" h="100%">
                                                            <video
                                                                src={URL.createObjectURL(creative.file)}
                                                                muted
                                                                preload="metadata"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                            <Center
                                                                pos="absolute"
                                                                style={{
                                                                    inset: 0,
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                                                }}
                                                            >
                                                                <IconPlayerPlay size={16} color="white" />
                                                            </Center>
                                                        </Box>
                                                    ) : (
                                                        <IconPhoto size={24} />
                                                    )}
                                                </Paper>
                                            </Box>
                                            <Box miw={0}>
                                                <Text truncate maw={160}>
                                                    {creative.file.name}
                                                </Text>
                                                <Text size="xs" c="dimmed">
                                                    {creative.file.type}
                                                </Text>
                                                <Text size="xs" c="dimmed" display="block">
                                                    {(creative.file.size / 1024 / 1024).toFixed(2)} MB
                                                </Text>
                                            </Box>
                                        </Group>
                                    </Table.Td>

                                    {/* Week */}
                                    <Table.Td>
                                        <DatePickerInput
                                            value={
                                                creative.marketingFields.week
                                                    ? dayjs(creative.marketingFields.week).toDate()
                                                    : null
                                            }
                                            onChange={(date) => {
                                                updateCreativeField(
                                                    creative.id,
                                                    'week',
                                                    date ? dayjs(date).format('YYYY-MM-DD') : '',
                                                );
                                            }}
                                            error={hasFieldError(creative, 'week') || undefined}
                                            disabled={isUploading}
                                            required
                                            clearable
                                        />
                                    </Table.Td>

                                    {/* Text */}
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Checkbox
                                            checked={creative.marketingFields.hasTextOverlay}
                                            onChange={(e) =>
                                                updateCreativeField(
                                                    creative.id,
                                                    'hasTextOverlay',
                                                    e.currentTarget.checked,
                                                )
                                            }
                                            disabled={isUploading}
                                        />
                                    </Table.Td>

                                    {/* CTA */}
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Checkbox
                                            checked={creative.marketingFields.hasCallToAction}
                                            onChange={(e) =>
                                                updateCreativeField(
                                                    creative.id,
                                                    'hasCallToAction',
                                                    e.currentTarget.checked,
                                                )
                                            }
                                            disabled={isUploading}
                                        />
                                    </Table.Td>

                                    {/* Freeholder */}
                                    <Table.Td>
                                        <TextInput
                                            value={creative.marketingFields.additionalText || ''}
                                            onChange={(e) =>
                                                updateCreativeField(
                                                    creative.id,
                                                    'additionalText',
                                                    e.currentTarget.value,
                                                )
                                            }
                                            disabled={isUploading}
                                        />
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>

            <Divider mt="md" />
            <Group justify="flex-end" gap="sm" mt="md">
                <Button onClick={onCancel} variant="outline" disabled={isUploading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={isUploading || !allCreativesValid}
                    leftSection={isUploading ? <Loader size={16} /> : <IconCheck size={16} />}
                >
                    {isUploading
                        ? 'Uploading...'
                        : `Upload ${creatives.length} ${plural(creatives.length, 'Creative', 'Creatives')}`}
                </Button>
            </Group>
        </Modal>
    );
};
